/**
 *  Component: TagsList
 */
import React, { useEffect, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { Paper, Table, TableBody, TableRow, TableContainer, TableHead } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { PlainTextHeader } from 'Deer/components/HeaderComponents/PlainTextHeader';
import { L2_PATHS } from 'Deer/constants/routes';
import { PlatformIntentDropdown } from 'Deer/intent/Settings/Platform/PlatformIntentDropdown';
import { Layout } from 'Deer/intent/components/Layout/Layout';
import HashTag from 'models/hashtag';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { HeaderTableCell } from 'screens/Team/TeamTasksListView/styles';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import { StyledBorderedPaper, VerticallyAlignedFlexContainer } from 'styles/common';
import { checkSettingsPageRevampEnabled } from 'utils/helpers/featureFlags';

import { sendPageViewEvent } from 'constants/mixpanelHelper';

import { getOrganizationGrades, getOrganizationFunctions } from 'services/admin';
import { showErrorSnackbar } from 'services/snackbar';

import { Typography7 } from 'uiToolkit/Typography';

import { SkeletonContainer } from '../MeshTags/styles';
import FunctionAndGradeEditableRow from './FunctionAndGradeEditableRow';
import NoFunctionAndGradeComponent from './NoFunctionAndGradeComponent';
import ReorderConfirmationModal from './ReorderConfirmationModal';

const FUNCTION_AND_GRADE_BREADCRUMBS = {
  items: [
    {
      label: 'Company',
      to: L2_PATHS.company.overview,
    },
  ],
  page: {
    label: 'Platform Settings',
    to: '',
  },
};

const FunctionAndGradeList = ({
  tagType,
  tagTypeLabel,
  singularTagTypeLabel,
  tagLabel,
  isEnterpriseAccessOnly,
  allowDescriptionNewline = false,
}) => {
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);

  const [allTags, setAllTags] = useState(null);
  const [tags, setTags] = useState(null);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [addNew, setAddNew] = useState(false);
  const [newTag, setNewTag] = useState(new HashTag());
  const rowsPerPageOptions = [5, 10, 15];
  const teamId = null;
  const dispatch = useDispatch();

  // functions and levels
  const [functions, setFunctions] = useState(null);
  const [gradesForOrg, setGradesForOrg] = useState(null);
  const [loadingFunctions, setLoadingFunctions] = useState(true);
  const fetchData = () => {
    setLoadingFunctions(true);
    getOrganizationFunctions()
      .then((res) => {
        setFunctions(res);
        getOrganizationGrades().then((res) => setGradesForOrg(res));
      })
      .catch((err) => {
        console.error('Error in fetching functions : ', err);
        dispatch(showErrorSnackbar(err.message));
      })
      .finally(() => {
        setLoadingFunctions(false);
      });
  };
  useEffect(() => {
    fetchData();
  }, []);
  // end of functions and levels

  const disableExpansion = (tagId) => {
    setTags(
      tags.map((tag) => {
        if (tagId === tag.id) tag.hasChildTags = false;
        return tag;
      })
    );
  };

  const enableExpansion = (tagId) => {
    setAllTags(
      allTags.map((tag) => {
        if (tagId === tag.id) tag.hasChildTags = true;
        return tag;
      })
    );
  };

  useEffect(() => {
    sendPageViewEvent({
      eventProperties: {
        source: 'Company-settings-function and grade',
      },
    });
  }, []);

  const HeaderComp = () => (
    <VerticallyAlignedFlexContainer>
      <Box>
        <PlainTextHeader>{tagTypeLabel}</PlainTextHeader>
      </Box>

      {/* <TooltipInfo
      leftGutter={1}
      title={KH_LINKS.TASK_TAGS.description}
      linkUrl={KH_LINKS.TASK_TAGS.url}
    /> */}
    </VerticallyAlignedFlexContainer>
  );

  const isSettingsPageRevampEnabled = checkSettingsPageRevampEnabled();

  if (loadingFunctions && !functions) {
    return (
      <Layout
        Filters={
          isSettingsPageRevampEnabled ? <PlatformIntentDropdown breadcrumbs={FUNCTION_AND_GRADE_BREADCRUMBS} /> : false
        }
      >
        <Container>
          {!isSettingsPageRevampEnabled && <HeaderComp />}
          <StyledBorderedPaper style={{ marginTop: '8px', overflow: 'auto' }} elevation={0}>
            <SkeletonContainer>
              <Skeleton width="40%" />
              <Skeleton width="30%" />
              <Skeleton width="30%" />
            </SkeletonContainer>
          </StyledBorderedPaper>
        </Container>
      </Layout>
    );
  }

  if ((!functions || !functions?.length) && !loadingFunctions) {
    return (
      <Layout
        Filters={
          isSettingsPageRevampEnabled ? <PlatformIntentDropdown breadcrumbs={FUNCTION_AND_GRADE_BREADCRUMBS} /> : false
        }
      >
        <Container>
          {!isSettingsPageRevampEnabled && <HeaderComp />}
          <NoFunctionAndGradeComponent />
        </Container>
      </Layout>
    );
  }

  return (
    <Layout
      Filters={
        isSettingsPageRevampEnabled ? <PlatformIntentDropdown breadcrumbs={FUNCTION_AND_GRADE_BREADCRUMBS} /> : false
      }
    >
      <Container>
        {!isSettingsPageRevampEnabled && <HeaderComp />}
        <StyledBorderedPaper style={{ marginTop: '8px', overflow: 'auto' }} elevation={0}>
          <>
            <StyledTableContainer>
              <Table size="small">
                <HeaderComponent tagLabel={tagLabel} />
                {functions && (
                  <TableBody>
                    {functions.map((func, idx) => (
                      <FunctionAndGradeEditableRow
                        key={`${func.function.uuid}-function-row`}
                        func={func}
                        isExpandable={func?.jobGradeList?.length}
                        isEditing={func.isEditing}
                        disableExpansion={() => disableExpansion(func.uuid)}
                        enableExpansion={() => enableExpansion(func.uuid)}
                        hasChildTags={func.hasChildTags}
                        canHaveChildTags
                        allowDescriptionNewline={allowDescriptionNewline}
                        loadingFunctions={loadingFunctions}
                        gradesForOrg={gradesForOrg}
                        refetchData={fetchData}
                        idx={idx}
                        levelsForWhichLinesToBeDrawn={[]}
                      />
                    ))}
                    {addNew && (
                      <FunctionAndGradeEditableRow
                        tag={newTag}
                        isExpandable={tagType === 'GOAL'}
                        isEditing
                        enableExpansion={enableExpansion}
                        hasChildTags={false}
                        canHaveChildTags={false}
                        allowDescriptionNewline={allowDescriptionNewline}
                        loadingFunctions={loadingFunctions}
                        levelsForWhichLinesToBeDrawn={[]}
                      />
                    )}
                  </TableBody>
                )}
              </Table>
              <ReorderConfirmationModal />
            </StyledTableContainer>
          </>
        </StyledBorderedPaper>
        <StyledBottomBar>
          <ButtonContainer>
            {/* <Button
              blue
              size="large"
              disabled={addNew || (isEnterpriseAccessOnly && !isEnterpriseUser)}
            >
              Create New Function
            </Button> */}
          </ButtonContainer>

          {/* <TablePagination
            component="div"
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            rowsPerPageOptions={rowsPerPageOptions}
            onChangePage={(e, newPage) => {
              setPage(newPage);
              setPaginatedTags(newPage, allTags, rowsPerPage);
            }}
            onChangeRowsPerPage={e => {
              setRowsPerPage(e.target.value);
              setPaginatedTags(page, allTags, e.target.value);
            }}
            style={{ marginLeft: 'auto' }}
          /> */}
        </StyledBottomBar>
      </Container>
    </Layout>
  );
};

const HeaderComponent = ({ isExpandable = false, tagLabel }) => (
  <StyledTableHead>
    <TableRow>
      {/* { isExpandable && 
          <HeaderTableCell width='10%'></HeaderTableCell>
        } */}
      {/* <HeaderTableCell width="10%">Active</HeaderTableCell> */}
      <HeaderTableCell paddingtop="0.75rem" paddingBottom="0.75rem" paddingLeft="1.5rem" width="40%">
        <Typography7 color="textSecondary">Function</Typography7>
      </HeaderTableCell>
      <HeaderTableCell paddingTop="0.75rem" paddingLeft="0.5rem" paddingBottom="0.75rem" width="30%">
        <Typography7 color="textSecondary">Grade</Typography7>
      </HeaderTableCell>
      <HeaderTableCell paddingTop="0.75rem" paddingBottom="0.75rem" width="30%">
        <Typography7 color="textSecondary">Role</Typography7>
      </HeaderTableCell>
      {/* <HeaderTableCell paddingtop="0.75rem" paddingBottom="0.75rem" width="10%"></HeaderTableCell> */}
    </TableRow>
  </StyledTableHead>
);

const StyledTableHead = styled(TableHead)`
  background: ${(props) => props.theme.palette.background.paper};
  box-shadow: 0px 2px ${(props) => props.theme.palette.borders.lighterGrey};
  margin-bottom: 1rem;
`;

const Container = styled.div`
  // margin: 0.5rem 0 2rem 0;
`;

const StyledPaper = styled(Paper)`
  margin-top: 1rem;
`;

const StyledBottomBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
`;

const ButtonContainer = styled.div`
  position: absolute;
  right: 2rem;
  top: 33px;
  button {
    padding: 0.3rem 2rem;
  }
`;
const StyledTableContainer = styled(TableContainer)`
  min-width: 1000px;
`;
export default FunctionAndGradeList;
